import {
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  effect,
  signal,
} from '@angular/core';
import { NgModel } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatTabChangeEvent, MatTabGroup } from '@angular/material/tabs';
import { AnalyticsEventsService } from '@techspert-io/analytics';
import { CognitoAuthService } from '@techspert-io/auth';
import { IExpertCallAction } from '@techspert-io/call-actions';
import { ExpertFile } from '@techspert-io/expert-files';
import {
  ExpertUpdateType,
  ExpertsService,
  IExpertReadyToAcceptReturn,
} from '@techspert-io/experts';
import { IOpportunity, OpportunityService } from '@techspert-io/opportunities';
import { ITimeSlotTimestamp } from '@techspert-io/schedule-calendar';
import { SharedExpertEmailsService } from '@techspert-io/shared-expert-emails';
import { ToastrService } from 'ngx-toastr';
import { EMPTY, timer } from 'rxjs';
import {
  catchError,
  finalize,
  mergeMap,
  switchMap,
  takeWhile,
  tap,
} from 'rxjs/operators';
import { UserPermission } from '../../../../features/auth/models/permissions';
import { CallSchedulePeriod } from '../../../../features/interview-period/models/interview-period.models';
import { IExpert } from '../../../../shared/models/expert.interface';
import { RequestEngagementService } from '../../../../shared/services/request-engagement.service';
import { TimezonePreferenceService } from '../../../../shared/services/timezone-preference.service';
import { ExpertStoreService } from '../../../../shared/state/expert.service';
import { AcceptanceWithdrawalBlockedDialogComponent } from './dialogs/acceptance-withdrawal-blocked-dialog/acceptance-withdrawal-blocked-dialog.component';
import { ExpertNoShowDialogComponent } from './dialogs/expert-no-show-dialog/expert-no-show-dialog.component';
import {
  ExpertTranscriptDialogComponent,
  IExpertTranscriptDialogData,
  ITranscriptSeekTo,
} from './dialogs/expert-transcript-dialog/expert-transcript-dialog.component';
import { OnHoldReasonDialogComponent } from './dialogs/reason-for-on-hold/on-hold-reason-dialog.component';
import { ReasonRejectionDialogComponent } from './dialogs/reason-for-rejection/rejection-reason-dialog.component';
import {
  IRequestAdditionalAvailabilityDialogData,
  RequestAdditionalAvailabilityComponent,
} from './dialogs/request-additional-availability/request-additional-availability.component';
import {
  IRequestConferenceDialogData,
  IRequestConferenceDialogResult,
  RequestConferenceDialogComponent,
} from './dialogs/request-conference-dialog/request-conference-dialog.component';
import {
  ISelectAvailabilityDialogData,
  SelectAvailabilityDialogComponent,
} from './dialogs/select-availability-dialog/select-availability-dialog.component';
import { ShareExpertDialogComponent } from './dialogs/share-expert/share-expert-dialog.component';
import {
  ICallOption,
  IViewTranscriptEvent,
} from './expert-quotes/expert-quotes.component';

type OnHoldRes = {
  onHoldReason: string;
  onHold: boolean;
};

type RejectionRes = {
  rejectionReason: string;
  rejected: boolean;
};

enum ExpertTab {
  CallSummary = 'call-summary',
  SavedQuotes = 'saved-quotes',
  Screener = 'screener',
  Biography = 'biography',
  Availability = 'availability',
  FollowUpQuestions = 'follow-up-questions',
  Comments = 'comments',
  RejectionReason = 'rejection-reason',
  Downloads = 'downloads',
}

@Component({
  selector: 'app-expert',
  templateUrl: './expert.component.html',
  styleUrls: ['./expert.component.scss'],
  standalone: false,
})
export class ExpertComponent implements OnInit, OnChanges {
  @Input() expertData: IExpert;
  @Input() opportunity: IOpportunity;
  @Input() viewStage: string;
  @Input() isExpanded: boolean;
  @Input() isExpertApprovalRequired: boolean;

  @Output() askFollowUpQuestionSignal = new EventEmitter<void>();

  @ViewChild('clientNotes') clientNotes: NgModel;
  @ViewChild('expertTabGroup', { static: false }) expertTabGroup: MatTabGroup;

  @HostListener('window:resize', ['$event'])
  onResize(_?: Event): void {
    this.smallScreen = window.innerWidth <= 1280;
  }

  private callTranscripts: Record<
    string,
    { transcriptFile: ExpertFile; audioFile?: ExpertFile }
  > = {};

  confirmClicked: boolean;
  smallScreen: boolean;
  requestNewEngagementSent: boolean;
  expertFilePrefix: string = '';
  isCallInProgress: boolean = false;
  expertIcon: string = 'new';
  isAcceptAvailabilityInProgress = false;
  /** The time being accepted, until its dialog opens */
  pendingAvailability: ITimeSlotTimestamp | null = null;

  unitsMapping: Record<string, string> = {
    '=1': '1 unit',
    other: '# units',
  };

  readonly collapseActions = signal(false);

  readonly selectedTimezone = this.timezonePreferenceService.selectedTimezone;

  get ExpertTab() {
    return ExpertTab;
  }

  get transcriptFileIds(): string[] {
    return Object.keys(this.callTranscripts);
  }

  get calls(): ICallOption[] {
    const entries = Object.entries(this.callTranscripts);

    return entries
      .map(([groupId, call], index) => ({
        groupId,
        transcriptFileId: call.transcriptFile?.fileId,
        callNumber: entries.length - index,
      }))
      .filter((call): call is ICallOption => !!call.transcriptFileId);
  }

  get canRejectExperts() {
    return this.cognitoAuthService.loggedInUser?.permissions.has(
      UserPermission.ExpertsReject
    );
  }

  get additionalAvailabilityRequestText() {
    const expertName =
      this.expertData?.firstName || this.expertData?.lastName || 'the expert';

    if (this.expertData?.requestedAnotherTime) {
      return {
        title: 'Additional availability requested',
        description: `We'll let you know when ${expertName} adds new times.`,
      };
    }

    return {
      title: 'None of these times work?',
      description: `Ask ${expertName} for additional availability. New times will appear here.`,
    };
  }

  constructor(
    private dialog: MatDialog,
    private requestEngagementService: RequestEngagementService,
    private expertStoreService: ExpertStoreService,
    private opportunityService: OpportunityService,
    private expertsService: ExpertsService,
    private timezonePreferenceService: TimezonePreferenceService,
    private toastService: ToastrService,
    private shareExpertActionsService: SharedExpertEmailsService,
    private analyticsEventsService: AnalyticsEventsService,
    private cognitoAuthService: CognitoAuthService
  ) {
    effect(() => {
      this.selectedTimezone();
    });
  }

  ngOnInit(): void {
    this.onResize();
    this.expertFilePrefix = this.constructExpertFilePrefix();
    this.setupIcon();
    this.resolveTranscriptFiles();
  }

  ngOnChanges(sc: SimpleChanges): void {
    if (sc.isExpanded?.currentValue) {
      setTimeout(() => {
        this.expertTabGroup.realignInkBar();
      }, 500);
    }

    if (
      sc.expertData?.previousValue?.clientApproved !==
      sc.expertData?.currentValue?.clientApproved
    ) {
      this.setupIcon();
    }

    if (sc.expertData) {
      this.resolveTranscriptFiles();
    }

    this.checkCurrentAction();
  }

  onTabChange(event: MatTabChangeEvent) {
    this.collapseActions.set(event.tab.textLabel === ExpertTab.Availability);
  }

  onViewTranscript({ groupId, startTime }: IViewTranscriptEvent): void {
    this.openCallTranscript(this.callTranscripts[groupId], {
      time: startTime,
    });
  }

  viewCallTranscript(groupId?: string): void {
    this.openCallTranscript(
      groupId
        ? this.callTranscripts[groupId]
        : Object.values(this.callTranscripts).find(Boolean)
    );
  }

  requestExpertEngagement(expert: IExpert): void {
    this.requestEngagementService
      .request(expert.expertId, 'opportunity_page')
      .pipe(tap(() => (this.requestNewEngagementSent = true)))
      .subscribe();
  }

  shareExpert(expert: IExpert): void {
    this.shareExpertImpl(expert);
  }

  expertNoShow(expert: IExpert): void {
    this.dialog.open<
      ExpertNoShowDialogComponent,
      { expert: IExpert; timezone: string },
      string
    >(ExpertNoShowDialogComponent, {
      width: '520px',
      minHeight: '200px',
      data: { expert, timezone: this.selectedTimezone() },
      autoFocus: false,
    });
  }

  saveClientNotes(clientNotes: string): void {
    this.expertStoreService.updateExpert({
      expertId: this.expertData.expertId,
      type: ExpertUpdateType.UpdateClientNotes,
      payload: { clientNotes },
    });
    this.clientNotes.reset(clientNotes);
  }

  toggleRejectExpert(clientRejected: boolean, expertId: string): void {
    if (!this.canRejectExperts) {
      return;
    }

    if (clientRejected) {
      this.dialog
        .open<ReasonRejectionDialogComponent, unknown, RejectionRes>(
          ReasonRejectionDialogComponent,
          { width: '500px', autoFocus: false }
        )
        .afterClosed()
        .pipe(takeWhile((v) => !!v))
        .subscribe(({ rejectionReason, rejected }) => {
          this.expertStoreService.updateExpert({
            expertId,
            type: ExpertUpdateType.ExpertRejection,
            payload: {
              clientRejected: rejected,
              ...(rejectionReason ? { rejectionReason } : {}),
            },
          });
        });
    } else {
      this.expertStoreService.updateExpert({
        expertId,
        type: ExpertUpdateType.ExpertRejection,
        payload: { clientRejected },
      });
    }
  }

  toggleCollapseActions() {
    this.collapseActions.update((collapsed) => !collapsed);
  }

  toggleOnHold(onHold: boolean, expertId: string): void {
    if (onHold) {
      this.dialog
        .open<OnHoldReasonDialogComponent, unknown, OnHoldRes>(
          OnHoldReasonDialogComponent,
          { width: '500px', autoFocus: false }
        )
        .afterClosed()
        .pipe(takeWhile((v) => !!v))
        .subscribe(({ onHoldReason, onHold }) => {
          this.expertStoreService.updateExpert({
            payload: { onHold, ...(onHoldReason ? { onHoldReason } : {}) },
            type: ExpertUpdateType.ExpertOnHold,
            expertId,
          });
        });
    } else {
      this.expertStoreService.updateExpert({
        payload: { onHold },
        type: ExpertUpdateType.ExpertOnHold,
        expertId,
      });
    }
  }

  toggleApproval(expert: IExpert): void {
    if (expert.clientApproved && this.viewStage !== 'new') {
      this.dialog.open<AcceptanceWithdrawalBlockedDialogComponent>(
        AcceptanceWithdrawalBlockedDialogComponent,
        {
          width: '450px',
          height: '220px',
        }
      );
      return;
    }

    this.expertStoreService.updateExpert({
      expertId: expert.expertId,
      type: ExpertUpdateType.ExpertApproval,
      payload: { clientApproved: !expert.clientApproved },
    });
  }

  acceptAvailability(
    availability: ITimeSlotTimestamp,
    overlappingCalls: CallSchedulePeriod[] = []
  ) {
    if (this.isAcceptAvailabilityInProgress) {
      return;
    }

    if (this.isPastDate(availability.start)) {
      return;
    }

    if (this.isExpertApprovalRequired && !this.expertData.clientApproved) {
      this.toastService.warning(
        'This expert needs approval before you are able to schedule a call',
        'Compliance approval required'
      );
      return;
    }

    this.scheduleOrConfirmExpert(availability, overlappingCalls);
  }

  requestDifferentTime(expert: IExpert): void {
    if (this.isExpertApprovalRequired && !this.expertData.clientApproved) {
      this.toastService.warning(
        'This expert needs approval before you are able to request a consultation',
        'Compliance approval required'
      );
      return;
    }

    this.dialog
      .open<
        RequestAdditionalAvailabilityComponent,
        IRequestAdditionalAvailabilityDialogData
      >(RequestAdditionalAvailabilityComponent, {
        maxHeight: '90vh',
        width: '680px',
        data: {
          opportunity: this.opportunity,
          expert,
        },
      })
      .afterClosed()
      .subscribe();
  }

  requestBio(): void {
    this.expertStoreService.updateExpert({
      type: ExpertUpdateType.RequestBio,
      expertId: this.expertData.expertId,
    });
  }

  isPastDate(date: string): boolean {
    return new Date(date).getTime() < new Date().getTime();
  }

  checkCurrentAction(): void {
    const currentAction = this.getCurrentAction();
    const upcomingAction = this.getUpcomingAction();

    const now = new Date().getTime();
    if (currentAction) {
      this.isCallInProgress = true;

      timer(this.getActionEndTime(currentAction) - now).subscribe(
        () => (this.isCallInProgress = false)
      );
    } else if (upcomingAction) {
      timer(this.getActionStartTime(upcomingAction) - now).subscribe(
        () => (this.isCallInProgress = true)
      );

      timer(this.getActionEndTime(upcomingAction) - now).subscribe(
        () => (this.isCallInProgress = false)
      );
    }
  }

  askFollowUpQuestions(): void {
    this.askFollowUpQuestionSignal.emit();
  }

  private scheduleOrConfirmExpert(
    availability: ITimeSlotTimestamp,
    overlappingCalls: CallSchedulePeriod[]
  ) {
    this.isAcceptAvailabilityInProgress = true;
    this.pendingAvailability = availability;

    this.expertsService
      .isExpertReadyToAccept(this.expertData.expertId, availability)
      .pipe(
        mergeMap((readyToAccept) =>
          this.opportunity.automatedScheduling && readyToAccept.isReady
            ? this.scheduleExpert(availability, overlappingCalls)
            : this.confirmExpert(availability, readyToAccept, overlappingCalls)
        ),
        catchError((err) => {
          console.error(err);
          this.toastService.error(
            'Please try again later or contact your Project Manager if the issue persists',
            'Failed to accept availability'
          );
          return EMPTY;
        }),
        finalize(() => {
          this.analyticsEventsService.sendEvent('click', {
            event_category: 'call_scheduled',
            value: this.expertData.expertId,
          });

          this.isAcceptAvailabilityInProgress = false;
          this.pendingAvailability = null;
        })
      )
      .subscribe();
  }

  private shareExpertImpl(expert: IExpert) {
    interface ISharedExpertDialogRes {
      recipients: string[];
      notes: string;
    }

    this.dialog
      .open<ShareExpertDialogComponent, undefined, ISharedExpertDialogRes>(
        ShareExpertDialogComponent,
        { width: '520px', autoFocus: false }
      )
      .afterClosed()
      .pipe(
        takeWhile((v) => !!v),
        switchMap((res) =>
          this.shareExpertActionsService.share(expert.expertId, res)
        ),
        tap(() => this.toastService.success('Expert successfully shared'))
      )
      .subscribe();
  }

  private scheduleExpert(
    availability: ITimeSlotTimestamp,
    overlappingCalls: CallSchedulePeriod[]
  ) {
    return this.opportunityService
      .getClientContacts(this.opportunity.publicDisplayId)
      .pipe(
        switchMap((contacts) => {
          const dialogRef = this.dialog.open<
            RequestConferenceDialogComponent,
            IRequestConferenceDialogData,
            IRequestConferenceDialogResult
          >(RequestConferenceDialogComponent, {
            width: '580px',
            data: {
              contacts,
              callTime: availability.start,
              expertTimezone: this.expertData.timezoneName,
              hostedCall: !!this.opportunity.isClientHostEnabled,
              overlappingCalls,
              timezone: this.selectedTimezone(),
            },
            autoFocus: false,
          });

          this.pendingAvailability = null;

          return dialogRef.afterClosed().pipe(
            takeWhile((outcome) => !!outcome),
            tap((res) => {
              this.expertStoreService.updateExpert({
                expertId: this.expertData.expertId,
                payload: {
                  primaryRecipient: res.primaryRecipient,
                  hosts: res.hosts,
                  guests: res.guests,
                  callTime: availability,
                  clientTimezone: res.clientTimezone,
                },
                type: ExpertUpdateType.ExpertSchedule,
              });
            })
          );
        })
      );
  }

  private confirmExpert(
    availability: ITimeSlotTimestamp,
    readyToAccept: IExpertReadyToAcceptReturn,
    overlappingCalls: CallSchedulePeriod[]
  ) {
    const dialogRef = this.dialog.open<
      SelectAvailabilityDialogComponent,
      ISelectAvailabilityDialogData,
      string
    >(SelectAvailabilityDialogComponent, {
      width: '450px',
      data: {
        availability: availability.start,
        readyToAccept,
        timezone: this.selectedTimezone(),
        overlappingCalls,
      },
      autoFocus: false,
    });

    this.pendingAvailability = null;

    return dialogRef.afterClosed().pipe(
      takeWhile((outcome) => !!outcome),
      tap(() => {
        this.expertStoreService.updateExpert({
          expertId: this.expertData.expertId,
          payload: {
            callTime: availability,
            clientTimezone: this.selectedTimezone(),
          },
          type: ExpertUpdateType.ConfirmAvailability,
        });
      })
    );
  }

  private resolveTranscriptFiles(): void {
    this.callTranscripts = this.expertData.uploadedFiles
      .filter((file) => file.type === 'enhancedTranscript')
      .sort((a, b) => b.dateCreated - a.dateCreated)
      .map((file) => file.groupId)
      .reduce<
        Record<string, { transcriptFile: ExpertFile; audioFile?: ExpertFile }>
      >(
        (acc, groupId) => ({
          ...acc,
          [groupId]: {
            transcriptFile: this.expertData.uploadedFiles.find(
              (file) =>
                file.groupId === groupId && file.type === 'enhancedTranscript'
            ),
            audioFile: this.expertData.uploadedFiles.find(
              (file) =>
                file.groupId === groupId && file.type === 'zoomRecording'
            ),
          },
        }),
        {}
      );
  }

  private openCallTranscript(
    call: { transcriptFile: ExpertFile; audioFile?: ExpertFile } | undefined,
    seekTo?: ITranscriptSeekTo
  ): void {
    if (!call) {
      return;
    }

    this.dialog.open<
      ExpertTranscriptDialogComponent,
      IExpertTranscriptDialogData
    >(ExpertTranscriptDialogComponent, {
      width: '800px',
      autoFocus: false,
      data: {
        expertName: `${this.expertData.firstName} ${this.expertData.lastName}`,
        expertId: this.expertData.expertId,
        transcriptFile: call.transcriptFile,
        audioFile: call.audioFile,
        expertActionId: call.transcriptFile.groupId,
        seekTo,
      },
    });
  }

  private constructExpertFilePrefix(): string {
    return `${this.expertData.firstName.charAt(
      0
    )}${this.expertData.lastName.charAt(0)}`;
  }

  private setupIcon(): void {
    this.expertIcon =
      this.viewStage === 'new' &&
      this.isExpertApprovalRequired &&
      !this.expertData.clientApproved
        ? 'requires-approval'
        : this.viewStage;
  }

  private getUpcomingAction(): IExpertCallAction {
    const now = new Date().getTime();
    return (this.expertData.actions || []).find(
      (action) =>
        action.actionType === 'call' &&
        new Date(action.datetime).getTime() > now
    ) as IExpertCallAction;
  }

  private getCurrentAction(): IExpertCallAction {
    const now = new Date().getTime();
    return (this.expertData.actions || []).find(
      (action) =>
        action.actionType === 'call' &&
        now > this.getActionStartTime(action) &&
        now < this.getActionEndTime(action)
    ) as IExpertCallAction;
  }

  private getActionStartTime(action: IExpertCallAction): number {
    return new Date(action.datetime).getTime();
  }

  private getActionEndTime(action: IExpertCallAction): number {
    return this.getActionStartTime(action) + action.duration * 60 * 1000;
  }
}
